import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Grid,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Home as HomeIcon,
  Brush as BrushIcon,
  Receipt as ReceiptIcon,
  Agriculture as AgricultureIcon,
  Build as BuildIcon,
  LocationOn as LocationIcon,
  AccountBalance as AccountBalanceIcon,
  Visibility as ViewIcon,
  ArrowBack as BackIcon,
  AttachMoney as MoneyIcon,
  Token as TokenIcon,
  ContentCopy as CopyIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Project } from '../services/projectService';
import { useThemeMode } from '../contexts/ThemeContext';
import ProjectDetailsDrawer from './ProjectDetailsDrawer';

interface ProjectCardProps {
  project: Project;
  onInvest: (project: Project) => void;
  onView?: (project: Project) => void;
  variant?: 'default' | 'compact';
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onInvest }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const [isFlipped, setIsFlipped] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleCopyAddress = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy address:', error);
    }
  };

  const getAssetIcon = (projectType: string) => {
    switch (projectType) {
      case 'real_estate':
        return <HomeIcon />;
      case 'art':
        return <BrushIcon />;
      case 'invoice':
        return <ReceiptIcon />;
      case 'agriculture':
        return <AgricultureIcon />;
      case 'equipment':
        return <BuildIcon />;
      default:
        return <AccountBalanceIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'tokenized':
        return { bg: 'linear-gradient(135deg, #10B981, #059669)', color: 'white', border: 'rgba(16, 185, 129, 0.3)' };
      case 'approved':
        return { bg: 'linear-gradient(135deg, #3B82F6, #2563EB)', color: 'white', border: 'rgba(59, 130, 246, 0.3)' };
      case 'submitted':
        return { bg: 'linear-gradient(135deg, #F59E0B, #D97706)', color: 'white', border: 'rgba(245, 158, 11, 0.3)' };
      case 'rejected':
        return { bg: 'linear-gradient(135deg, #EF4444, #DC2626)', color: 'white', border: 'rgba(239, 68, 68, 0.3)' };
      default:
        return { bg: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)', color: 'text.secondary', border: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' };
    }
  };

  const formatProjectType = (projectType: string) => {
    return projectType.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const statusColors = getStatusColor(project.status);

  const handleFlip = () => {
    if (isMobile) {
      setDrawerOpen(true);
      return;
    }
    setIsFlipped(!isFlipped);
  };

  const renderFrontSide = () => (
    <motion.div
      initial={{ rotateY: 0 }}
      animate={{ rotateY: isFlipped ? 180 : 0 }}
      transition={{
        duration: 0.6,
        ease: [0.4, 0.0, 0.2, 1],
      }}
      style={{
        transformStyle: 'preserve-3d',
        position: 'absolute',
        width: '100%',
        height: '100%',
        backfaceVisibility: 'hidden',
      }}
    >
      <Card
        sx={{
          height: '100%',
          minHeight: '520px',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          background: isDark ? 'rgba(17, 24, 39, 0.85)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
          borderRadius: 3,
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'rgba(59, 130, 246, 0.3)',
            boxShadow: isDark ? '0 12px 40px rgba(30, 64, 175, 0.2)' : '0 12px 40px rgba(30, 64, 175, 0.12)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, #1E40AF, #3B82F6, #0EA5E9)',
            opacity: 0,
            transition: 'opacity 0.3s ease',
          },
          '&:hover::before': {
            opacity: 1,
          },
        }}
      >
        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
                }}
              >
                {getAssetIcon(project.project_type)}
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 500 }}>
                  {formatProjectType(project.project_type)}
                </Typography>
                <Chip
                  label={project.status.toUpperCase()}
                  size="small"
                  sx={{
                    display: 'block',
                    width: 'fit-content',
                    mt: 0.5,
                    background: statusColors.bg,
                    color: statusColors.color,
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    height: 20,
                    border: `1px solid ${statusColors.border}`,
                  }}
                />
              </Box>
            </Box>

            {project.risk_score && (
              <Tooltip title="Risk Score" arrow>
                <Box sx={{
                  textAlign: 'center',
                  p: 1,
                  borderRadius: 2,
                  background: project.risk_score.score < 30
                    ? 'rgba(16, 185, 129, 0.1)'
                    : project.risk_score.score < 60
                    ? 'rgba(245, 158, 11, 0.1)'
                    : 'rgba(239, 68, 68, 0.1)',
                  border: `1px solid ${project.risk_score.score < 30
                    ? 'rgba(16, 185, 129, 0.2)'
                    : project.risk_score.score < 60
                    ? 'rgba(245, 158, 11, 0.2)'
                    : 'rgba(239, 68, 68, 0.2)'}`,
                }}>
                  <SecurityIcon sx={{
                    fontSize: '0.875rem',
                    color: project.risk_score.score < 30 ? '#10B981' : project.risk_score.score < 60 ? '#F59E0B' : '#EF4444',
                    mb: 0.5,
                  }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: project.risk_score.score < 30 ? '#10B981' : project.risk_score.score < 60 ? '#F59E0B' : '#EF4444',
                      fontWeight: 700,
                      fontSize: '1rem',
                      lineHeight: 1,
                    }}
                  >
                    {project.risk_score.score}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>

          {/* Title */}
          <Typography
            variant="h6"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.3,
            }}
          >
            {project.title}
          </Typography>

          {/* Description */}
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              color: 'text.secondary',
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {project.description}
          </Typography>

          {/* Location */}
          {project.location && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
              <LocationIcon sx={{ fontSize: '0.875rem', color: '#0EA5E9' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {project.location}
              </Typography>
            </Box>
          )}

          {/* Stats */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
            mb: 2.5,
          }}>
            <Box sx={{
              textAlign: 'center',
              p: 1.5,
              borderRadius: 2,
              background: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.08)',
              border: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.12)'}`,
            }}>
              <MoneyIcon sx={{ fontSize: '1rem', color: '#3B82F6', mb: 0.5 }} />
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.65rem' }}>
                Valuation
              </Typography>
              <Typography variant="body2" sx={{ color: '#3B82F6', fontWeight: 700 }}>
                {formatCurrency(project.valuation_usd || 0)}
              </Typography>
            </Box>
            <Box sx={{
              textAlign: 'center',
              p: 1.5,
              borderRadius: 2,
              background: isDark ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.08)',
              border: `1px solid ${isDark ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.12)'}`,
            }}>
              <TrendingUpIcon sx={{ fontSize: '1rem', color: '#10B981', mb: 0.5 }} />
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.65rem' }}>
                Tokenized
              </Typography>
              <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 700 }}>
                {project.tokenized_percent || 0}%
              </Typography>
            </Box>
          </Box>

          {/* Token Info */}
          {(project.token_name || project.token_symbol) && (
            <Box sx={{
              background: isDark ? 'rgba(30, 64, 175, 0.1)' : 'rgba(30, 64, 175, 0.08)',
              borderRadius: 2,
              p: 2,
              mb: 2,
              border: `1px solid ${isDark ? 'rgba(30, 64, 175, 0.2)' : 'rgba(30, 64, 175, 0.15)'}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TokenIcon sx={{ fontSize: '0.875rem', color: '#60A5FA' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                  Token Details
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                {project.token_name && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem' }}>Name</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.8rem' }}>
                      {project.token_name}
                    </Typography>
                  </Box>
                )}
                {project.token_symbol && (
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem' }}>Symbol</Typography>
                    <Typography variant="body2" sx={{ color: '#60A5FA', fontWeight: 600, fontSize: '0.8rem' }}>
                      ${project.token_symbol}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Contract Address */}
          {project.smart_contract_address && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 0.5, fontSize: '0.65rem' }}>
                Contract
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#0EA5E9',
                    fontFamily: 'monospace',
                    fontSize: '0.7rem',
                  }}
                >
                  {project.smart_contract_address.slice(0, 8)}...{project.smart_contract_address.slice(-6)}
                </Typography>
                <Tooltip title={copySuccess ? "Copied!" : "Copy Address"}>
                  <IconButton
                    size="small"
                    onClick={() => project.smart_contract_address && handleCopyAddress(project.smart_contract_address)}
                    sx={{
                      color: 'text.secondary',
                      p: 0.5,
                      '&:hover': { color: '#0EA5E9' }
                    }}
                  >
                    <CopyIcon sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleFlip}
              startIcon={<ViewIcon />}
              sx={{
                flex: 1,
                borderColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
                color: 'text.secondary',
                fontSize: '0.8rem',
                '&:hover': {
                  borderColor: 'rgba(59, 130, 246, 0.5)',
                  background: 'rgba(59, 130, 246, 0.1)',
                  color: '#60A5FA',
                },
              }}
            >
              Details
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={() => onInvest(project)}
              disabled={project.status !== 'tokenized'}
              sx={{
                flex: 1,
                background: project.status === 'tokenized'
                  ? 'linear-gradient(135deg, #1E40AF, #3B82F6)'
                  : isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                color: project.status === 'tokenized' ? 'white' : 'text.disabled',
                fontSize: '0.8rem',
                boxShadow: project.status === 'tokenized'
                  ? '0 4px 14px rgba(30, 64, 175, 0.35)'
                  : 'none',
                '&:hover': {
                  background: project.status === 'tokenized'
                    ? 'linear-gradient(135deg, #1E3A8A, #2563EB)'
                    : isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                },
                '&:disabled': {
                  background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  color: 'text.disabled',
                },
              }}
            >
              {project.status === 'tokenized' ? 'Invest Now' : 'Coming Soon'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  const renderBackSide = () => (
    <motion.div
      initial={{ rotateY: -180 }}
      animate={{ rotateY: isFlipped ? 0 : -180 }}
      transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
      style={{
        transformStyle: 'preserve-3d',
        position: 'absolute',
        width: '100%',
        height: '100%',
        backfaceVisibility: 'hidden',
        transform: 'rotateY(180deg)',
        display: isMobile ? 'none' : 'block',
      }}
    >
      <Card
        sx={{
          height: '100%',
          minHeight: '520px',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          background: isDark ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          border: `1px solid rgba(59, 130, 246, 0.2)`,
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Back Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#60A5FA' }}>
              Project Details
            </Typography>
            <IconButton onClick={handleFlip} size="small" sx={{ color: 'text.secondary' }}>
              <BackIcon />
            </IconButton>
          </Box>

          {/* Project Type & Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              {getAssetIcon(project.project_type)}
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {formatProjectType(project.project_type)}
              </Typography>
              <Chip
                label={project.status.toUpperCase()}
                size="small"
                sx={{
                  background: statusColors.bg,
                  color: statusColors.color,
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20,
                }}
              />
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Tokenization Progress
              </Typography>
              <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 600 }}>
                {project.tokenized_percent || 0}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={project.tokenized_percent || 0}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #10B981, #34D399)',
                  borderRadius: 3,
                },
              }}
            />
          </Box>

          {/* Risk Analysis */}
          {project.risk_score && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" sx={{ mb: 1.5, color: '#60A5FA', fontWeight: 600, display: 'block' }}>
                Risk Analysis
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Overall</Typography>
                    <Typography variant="h6" sx={{ color: project.risk_score.score < 30 ? '#10B981' : project.risk_score.score < 60 ? '#F59E0B' : '#EF4444', fontWeight: 700 }}>
                      {project.risk_score.score}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Liquidity</Typography>
                    <Typography variant="h6" sx={{ color: '#0EA5E9', fontWeight: 700 }}>
                      {project.risk_score.liquidity_score}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Token Information */}
          {(project.token_name || project.token_symbol || project.smart_contract_address) && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" sx={{ mb: 1.5, color: '#60A5FA', fontWeight: 600, display: 'block' }}>
                Token Information
              </Typography>
              <Box sx={{ bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)', p: 2, borderRadius: 2 }}>
                {project.token_name && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>Name</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>{project.token_name}</Typography>
                  </Box>
                )}
                {project.token_symbol && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>Symbol</Typography>
                    <Typography variant="body2" sx={{ color: '#60A5FA', fontWeight: 600 }}>${project.token_symbol}</Typography>
                  </Box>
                )}
                {project.smart_contract_address && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>Contract</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#0EA5E9', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {project.smart_contract_address}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => project.smart_contract_address && handleCopyAddress(project.smart_contract_address)}
                        sx={{ color: 'text.secondary', p: 0.5 }}
                      >
                        <CopyIcon sx={{ fontSize: '0.875rem' }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Valuation Details */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)', borderRadius: 2 }}>
                  <MoneyIcon sx={{ fontSize: '1.25rem', color: '#3B82F6', mb: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Valuation</Typography>
                  <Typography variant="body2" sx={{ color: '#3B82F6', fontWeight: 600 }}>
                    {formatCurrency(project.valuation_usd || 0)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)', borderRadius: 2 }}>
                  <TokenIcon sx={{ fontSize: '1.25rem', color: '#10B981', mb: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Tokenized</Typography>
                  <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600 }}>
                    {project.tokenized_percent || 0}%
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Action Button */}
          <Button
            variant="contained"
            fullWidth
            onClick={() => onInvest(project)}
            disabled={project.status !== 'tokenized'}
            sx={{
              mt: 'auto',
              background: project.status === 'tokenized'
                ? 'linear-gradient(135deg, #1E40AF, #3B82F6)'
                : isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              color: project.status === 'tokenized' ? 'white' : 'text.disabled',
              '&:hover': {
                background: project.status === 'tokenized'
                  ? 'linear-gradient(135deg, #1E3A8A, #2563EB)'
                  : isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              },
              '&:disabled': {
                background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                color: 'text.disabled',
              },
            }}
          >
            {project.status === 'tokenized' ? 'Invest Now' : 'Coming Soon'}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <Box
      sx={{
        perspective: { xs: 'none', md: '1000px' },
        height: '100%',
        minHeight: '520px',
        position: 'relative',
      }}
    >
      <motion.div
        whileHover={{ y: -4 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        style={{ height: '100%', minHeight: '520px', position: 'relative' }}
      >
        <Box sx={{
          position: 'relative',
          height: '100%',
          minHeight: '520px',
          width: '100%',
          transformStyle: { xs: 'flat', md: 'preserve-3d' },
        }}>
          {renderFrontSide()}
          {renderBackSide()}
        </Box>
      </motion.div>

      <ProjectDetailsDrawer
        project={project}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onInvest={onInvest}
      />
    </Box>
  );
};

export default ProjectCard;
